import React, { useState, useMemo } from 'react';
import { Button, Input, Space, Typography, Tag, message, Card, Checkbox, Modal, Row, Col } from 'antd';
import { ScissorOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { enzymeLibrary, Enzyme } from '@/data/enzymeLibrary';
import { detectSequenceType, findCutSites, CutSite, normalizeSequenceInput } from '@/services/enzymeAnalysis.service';
import CutMap from '../../components/results/enzyme/CutMap';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

//

export default function EnzymeAnalysisPage() {
  const navigate = useNavigate();
  const [sequence, setSequence] = useState('');
  const [selectedEnzymes, setSelectedEnzymes] = useState<string[]>([]);
  const [analysisResult, setAnalysisResult] = useState<{ sites: CutSite[], sequence: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [enzymeSearch, setEnzymeSearch] = useState('');
  const [customEnzymes, setCustomEnzymes] = useState<Enzyme[]>([]);
  const [customSite, setCustomSite] = useState('');

  const sequenceType = useMemo(() => detectSequenceType(sequence), [sequence]);
  const cleanSequence = useMemo(() => normalizeSequenceInput(sequence), [sequence]);
  const sequenceLength = cleanSequence.length;
  const filteredEnzymes = useMemo(() => {
    const kw = enzymeSearch.trim().toUpperCase();
    let filtered = enzymeLibrary.filter(e => {
      if (e.type !== 'restriction') return false;
      // 始终显示已选中的酶
      if (selectedEnzymes.includes(e.name)) {
        return true;
      }
      // 如果有搜索关键词，显示匹配的未选酶
      if (kw) {
        return e.name.toUpperCase().includes(kw) || e.recognitionSite.toUpperCase().includes(kw);
      }
      // 没有搜索关键词且未选中，不显示
      return false;
    });
    
    // 排序：已选的在前，未选的在后
    return filtered.sort((a, b) => {
      const aSelected = selectedEnzymes.includes(a.name);
      const bSelected = selectedEnzymes.includes(b.name);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [enzymeSearch, selectedEnzymes]);

  // 选择变更由每行复选框单独处理

  // 重置所有设置
  const handleReset = () => {
    setSequence('');
    setSelectedEnzymes([]);
    setAnalysisResult(null);
    setIsLoading(false);
    setEnzymeSearch('');
    setCustomEnzymes([]);
    setCustomSite('');
  };

  const handleAnalyze = () => {
    if (!sequence.trim()) {
      message.warning('请输入序列');
      return;
    }
    if (sequenceType === 'unknown') {
      message.error('无法识别序列类型，请输入有效的核酸序列。');
      return;
    }
    if (sequenceType === 'protein') {
      message.error('当前仅支持核酸限制性内切酶位点分析，请输入核酸序列。');
      return;
    }

    // 必须至少选择一种酶或添加自定义序列
    if (selectedEnzymes.length === 0 && customEnzymes.length === 0) {
      message.warning('请至少选择一种酶或添加自定义序列');
      return;
    }

    setIsLoading(true);
    setAnalysisResult(null);

    // 打印调试信息
    console.log(`Analyzing ${sequenceType} sequence of length ${sequenceLength}`);
    console.log(`Using ${selectedEnzymes.length} selected enzymes + ${customEnzymes.length} custom`);
    
    // 一点延迟，以便UI更新
    setTimeout(() => {
      // 仅使用已选择的限制性内切酶
      const relevantEnzymes = enzymeLibrary.filter(enzyme => 
        enzyme.type === 'restriction' && selectedEnzymes.includes(enzyme.name)
      );

      const allEnzymes = [...relevantEnzymes, ...customEnzymes];
      console.log(`Selected ${allEnzymes.length} enzymes for analysis`);

      const sites = findCutSites(sequence, allEnzymes);
      console.log(`Analysis complete. Found ${sites.length} cut sites.`);
      
      setAnalysisResult({ sites, sequence });
      setIsLoading(false);
      
      if (sites.length === 0) {
        message.info('在当前条件下未找到酶切位点。');
      } else {
        message.success(`找到 ${sites.length} 个酶切位点！`);
        setIsModalOpen(true);
      }
    }, 100);
  };

  return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/cloning')}
          style={{ marginBottom: 16 }}
        >
          返回分子克隆
        </Button>
        
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <ScissorOutlined style={{ fontSize: '64px', color: '#f5222d', marginBottom: 16 }} />
          <Title level={2}>酶切位点分析</Title>
          <Paragraph style={{ fontSize: '16px', color: '#666' }}>
            限制性内切酶位点预测、酶切图谱分析、多重酶切设计等功能
          </Paragraph>
        </div>

        <Card title="序列输入与酶切位点分析">
          <Row gutter={24}>
            {/* 左侧：序列输入 */}
            <Col span={18}>
              <Space direction="vertical" style={{ width: '100%', height: '100%' }} size="middle">
                <div>
                  <Space align="center">
                    <Text strong>序列类型：</Text>
                    <Tag color={sequenceType === 'nucleic' ? 'blue' : sequenceType === 'protein' ? 'purple' : 'red'}>
                      {sequenceType === 'nucleic' ? '核酸' : sequenceType === 'protein' ? '蛋白质' : '未知'}
                    </Tag>
                    {sequenceLength > 0 && <Text type="secondary">长度: {sequenceLength}</Text>}
                  </Space>
                </div>
                
                <TextArea
                  rows={28}
                  value={sequence}
                  onChange={(e) => setSequence(e.target.value)}
                  placeholder="在此处输入或粘贴 FASTA 格式或原始核酸序列"
                  style={{ fontFamily: 'monospace', flex: 1 }}
                />
              </Space>
            </Col>
            
            {/* 右侧：酶列表筛选 */}
            <Col span={6}>
              <Space direction="vertical" style={{ width: '100%', height: '100%' }} size="middle">
                <div>
                  <Text strong style={{ fontSize: '16px' }}>酶切位点筛选</Text>
                </div>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Input 
                    placeholder="搜索名称或识别序列" 
                    value={enzymeSearch} 
                    onChange={(e) => setEnzymeSearch(e.target.value)} 
                    allowClear 
                  />
                  <Space wrap align="center" style={{ width: '100%' }}>
                    <Button 
                      size="small" 
                      onClick={() => {
                        const commonEnzymes = enzymeLibrary.filter(e => e.type === 'restriction' && e.isCommon).map(e => e.name);
                        setSelectedEnzymes(commonEnzymes);
                        setEnzymeSearch('');
                        message.success(`已选择 ${commonEnzymes.length} 个常用酶`);
                      }}
                    >快速选择常用酶</Button>
                    <Button 
                      size="small" 
                      onClick={() => {
                        setSelectedEnzymes([]);
                        setEnzymeSearch('');
                      }}
                    >清除选择</Button>
                  </Space>
                  <Text type="secondary" style={{ fontSize: '11px' }}>已选: {selectedEnzymes.length + customEnzymes.length}</Text>
                </Space>
                
                <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 8 }}>
                  {filteredEnzymes.map(e => (
                    <div key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}>
                      <Checkbox
                        checked={selectedEnzymes.includes(e.name)}
                        onChange={(ev) => {
                          const checked = ev.target.checked;
                          setSelectedEnzymes(prev => checked ? Array.from(new Set([...prev, e.name])) : prev.filter(n => n !== e.name));
                        }}
                      >{e.name}</Checkbox>
                      <span style={{ fontFamily: 'Monaco, Consolas, monospace', color: '#999', marginLeft: 4 }}>{e.recognitionSite}</span>
                    </div>
                  ))}
                </div>
                
                <div>
                  <Text strong>自定义序列</Text>
                  <Text type="secondary" style={{ fontSize: '11px', marginLeft: 8 }}>切割位置默认在第一个碱基前</Text>
                  <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size="small">
                    <Input.Search
                      placeholder="输入识别序列(支持正则)，按回车添加"
                      value={customSite}
                      onChange={(e) => setCustomSite(e.target.value)}
                      onSearch={() => {
                        const site = customSite.trim();
                        if (!site) { message.warning('请输入识别序列'); return; }
                        const name = `Custom_${customEnzymes.length + 1}`;
                        const item: Enzyme = { 
                          name, 
                          recognitionSite: site.toUpperCase(), 
                          cutPosition: 0, 
                          cutOffset: 0, 
                          type: 'restriction', 
                          isCommon: false 
                        };
                        setCustomEnzymes(prev => [...prev, item]);
                        setCustomSite('');
                        message.success(`已添加自定义序列: ${site}`);
                      }}
                      enterButton="添加"
                      size="small"
                    />
                  </Space>
                  {customEnzymes.length > 0 && (
                    <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8, marginTop: 8 }}>
                      {customEnzymes.map((e, idx) => (
                        <div key={e.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', gap: 8 }}>
                          <span style={{ fontSize: '12px', fontFamily: 'Monaco, Consolas, monospace', flex: 1 }}>{e.recognitionSite}</span>
                          <Button type="link" danger size="small" onClick={() => setCustomEnzymes(prev => prev.filter((_, i) => i !== idx))}>删除</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <Space align="center" wrap style={{ width: '100%', marginTop: 16 }}>
                  <Button 
                    type="primary"
                    icon={<ScissorOutlined />}
                    onClick={handleAnalyze} 
                    loading={isLoading} 
                    disabled={!sequence.trim()}
                    block
                  >
                    开始分析
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={handleReset} block>
                    重置
                  </Button>
                </Space>
              </Space>
            </Col>
          </Row>
        </Card>
        
        {/* 结果弹窗 */}
        <Modal
          title={
            <Space>
              <Text strong>分析结果</Text>
              {analysisResult && (
                <>
                  <Tag color="green">{analysisResult.sites.length} 个酶切位点</Tag>
                  {(selectedEnzymes.length > 0 || customEnzymes.length > 0) && (
                    <Tag color="blue">已选择: {selectedEnzymes.length + customEnzymes.length} 个酶</Tag>
                  )}
                </>
              )}
            </Space>
          }
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width="90%"
          style={{ top: 20 }}
          destroyOnClose
        >
          {analysisResult && (
            <CutMap sequence={analysisResult.sequence} cutSites={analysisResult.sites} />
          )}
        </Modal>
      </div>

  );
}

